import axios from "axios";
import useLogin from "hooks/useLogin";
import { URL_WORKFLOW } from "constants/urlApis";
import { endpointError } from "utils/codeErrorsTimeout";

export default function useWorkflowsDiagram() {
  const { jwt } = useLogin();
  const headers = {
    Authorization: `Bearer ${jwt}`,
  };
  const headersBpmnView = {
    "Content-Type": "application/xml",
    "Access-Control-Allow-Origin": "*",
  };

  /**
   * Creates a workflow by sending a POST request to the specified endpoint.
   * @param {Object} data - The data object containing the information needed to create the workflow.
   * @param {string} data.name - The name of the workflow (example property).
   * @param {string} data.description - A brief description of the workflow (example property).
   * @param {Array} data.steps - An array of steps involved in the workflow (example property).
   * @returns {Promise<Object>} The response from the server after creating the workflow.
   */
  const Created_workflow = async (data) => {
    const response = await axios.post(`${URL_WORKFLOW}/workflows/`, data, {
      headers,
      endpointError: endpointError.workflow.Created_workflow,
    });
    return response;
  };

  /**
   * Creates a workflow process by sending a POST request to the specified endpoint.
   * @param {Object} data - The data to be sent in the request body for creating the workflow process.
   * @param {string | number} id - The identifier for the workflow to which the file configuration is associated.
   * @returns {Promise<Object>} The response from the Axios POST request, which contains the result of the operation.
   * @throws {Error} Throws an error if the request fails or does not respond as expected.
   */
  const Created_workflow_proccess = async (data, id) => {
    const response = await axios.post(`${URL_WORKFLOW}/workflows/file-config/${id}/`, data, {
      headers,
      endpointError: endpointError.workflow.Created_workflow_proccess,
    });
    return response;
  };

  /**
   * Uploads an XML file to the server associated with a specified workflow ID.
   * @param {File} file - The XML file to be uploaded.
   * @param {string} id - The ID of the workflow to which the file will be associated.
   * @returns {Promise<Object>} A promise that resolves to the response from the server.
   * @throws {Error} Throws an error if the upload fails or an invalid response is received.
   */
  const upload_xml = async (file, id) => {
    const response = await axios.post(`${URL_WORKFLOW}/workflows/file-config/${id}/`, file, {
      headers,
      endpointError: endpointError.workflow.upload_xml,
    });

    return response;
  };

  /**
   * Deletes a workflow diagram by its ID.
   * This function sends a DELETE request to the specified endpoint to remove
   * the workflow diagram associated with the given ID.
   * @async
   * @function delete_workflow_diagram
   * @param {string|number} id - The ID of the workflow diagram to be deleted.
   * @returns {Promise<AxiosResponse>} The response from the delete request.
   * @throws {Error} Throws an error if the request fails or if the ID is invalid.
   */
  const delete_workflow_diagram = async (id) => {
    const response = await axios.delete(`${URL_WORKFLOW}/workflows/file-config/${id}/`, {
      headers,
      endpointError: endpointError.delete_workflow_diagram,
    });

    return response;
  };

  /**
   * Fetches the process view from the specified URL.
   * This function makes an asynchronous GET request to the specified `urlProcess`
   * endpoint to retrieve the BPMN (Business Process Model and Notation) view
   * of a process. It sends necessary headers and handles any errors that may
   * occur during the request.
   * @param {string} urlProcess - The URL of the process to retrieve the view for.
   * @returns {Promise<Object>} A promise that resolves to the response object  from the axios GET request.
   * @throws {Error} Throws an error if the request fails or if the response is not as expected.
   */
  const getProcessView = async (urlProcess) => {
    const response = await axios.get(`${urlProcess}`, {
      headersBpmnView,
      endpointError: endpointError.getProcessView,
    });

    return response;
  };

  return {
    Created_workflow,
    Created_workflow_proccess,
    upload_xml,
    delete_workflow_diagram,
    getProcessView,
  };
}
